import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Suppress ResizeObserver errors completely (harmless Radix UI issue)
const debounce = (callback, delay) => {
  let tid;
  return function (...args) {
    const ctx = self;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import Leases from './pages/Leases';
import Inbox from './pages/Inbox';
import Maintenance from './pages/Maintenance';
import Inspections from './pages/Inspections';
import OwnerPortal from './pages/OwnerPortal';
import TenantPortal from './pages/TenantPortal';
import AIActivity from './pages/AIActivity';
import Tenants from './pages/Tenants';
import Landlords from './pages/Landlords';

import './App.css';

function AuthHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleGoogleAuth } = useAuth();

  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleGoogleAuth(sessionId).then(result => {
        if (result.success) {
          window.location.hash = '';
          navigate('/dashboard');
        }
      });
    }
  }, [location, navigate, handleGoogleAuth]);

  return null;
}

function PrivateRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  
  if (user?.role === 'landlord') {
    return <Navigate to="/owner-portal" replace />;
  }
  if (user?.role === 'tenant') {
    return <Navigate to="/tenant-portal" replace />;
  }
  return <Dashboard />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <>
      <AuthHandler />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
        
        <Route path="/dashboard" element={
          <PrivateRoute>
            <DashboardRouter />
          </PrivateRoute>
        } />
        
        <Route path="/properties" element={
          <PrivateRoute>
            <Properties />
          </PrivateRoute>
        } />
        
        <Route path="/leases" element={
          <PrivateRoute>
            <Leases />
          </PrivateRoute>
        } />
        
        <Route path="/inbox" element={
          <PrivateRoute>
            <Inbox />
          </PrivateRoute>
        } />
        
        <Route path="/maintenance" element={
          <PrivateRoute>
            <Maintenance />
          </PrivateRoute>
        } />
        
        <Route path="/inspections" element={
          <PrivateRoute allowedRoles={['admin', 'agent']}>
            <Inspections />
          </PrivateRoute>
        } />
        
        <Route path="/tenants" element={
          <PrivateRoute allowedRoles={['admin', 'agent']}>
            <Tenants />
          </PrivateRoute>
        } />
        
        <Route path="/landlords" element={
          <PrivateRoute allowedRoles={['admin', 'agent']}>
            <Landlords />
          </PrivateRoute>
        } />
        
        <Route path="/owner-portal" element={
          <PrivateRoute allowedRoles={['landlord']}>
            <OwnerPortal />
          </PrivateRoute>
        } />
        
        <Route path="/tenant-portal" element={
          <PrivateRoute allowedRoles={['tenant']}>
            <TenantPortal />
          </PrivateRoute>
        } />
        
        <Route path="/ai-activity" element={
          <PrivateRoute allowedRoles={['admin', 'agent']}>
            <AIActivity />
          </PrivateRoute>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;